import { createFileRoute } from "@tanstack/react-router";
import { CampaignLanding, type FaqItem } from "@/components/sections/CampaignLanding";

const URL = "https://kentro.com.br/instagram";
const TITLE =
  "Kentro Sistemas Inteligentes | Atendimento e Vendas no Instagram e WhatsApp";
const DESC =
  "Responda Direct e WhatsApp no mesmo lugar com IA, CRM e automação. Transforme seguidores em clientes com a Kentro.";

const QUICK_BULLETS = [
  "Direct do Instagram + WhatsApp em uma só tela",
  "IA responde em segundos, 24/7",
  "Cada conversa vira lead no CRM",
  "Campanhas com link rastreável",
  "API Oficial — sem risco de banimento",
  "Sem fidelidade, cancele quando quiser",
];

const FAQS: FaqItem[] = [
  {
    q: "A Kentro responde Direct do Instagram automaticamente?",
    a: "Sim. A IA da Kentro responde Direct e comentários, qualifica o lead e, se preciso, repassa para um humano sem o cliente perceber.",
  },
  {
    q: "Funciona com meu WhatsApp atual?",
    a: "Funciona. Migramos seu número para a API Oficial mantendo histórico e contatos — você ganha automação sem perder o que já construiu.",
  },
  {
    q: "Consigo medir quantas vendas vieram do Instagram?",
    a: "Sim. Cada conversa entra no CRM com origem identificada e os relatórios mostram qual conteúdo, campanha ou Story gerou venda.",
  },
  {
    q: "Preciso de equipe técnica para usar?",
    a: "Não. A implantação é guiada pelo nosso time, e a operação do dia a dia é simples — feita para times de marketing e atendimento.",
  },
  {
    q: "Quanto custa?",
    a: "Depende do volume de mensagens e canais. Fale com um especialista pelo WhatsApp para receber uma proposta personalizada — a demo é gratuita.",
  },
];

export const Route = createFileRoute("/instagram")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Kentro: Direct + WhatsApp + IA para vender mais" },
      {
        property: "og:description",
        content:
          "Centralize Instagram, WhatsApp e CRM com IA. Atenda mais rápido e transforme conversas em vendas.",
      },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify([
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Kentro Sistemas Inteligentes",
            url: "https://kentro.com.br",
            logo: "https://kentro.com.br/og-image.png",
            sameAs: [
              "https://www.linkedin.com/company/kentro-sistemas",
              "https://www.instagram.com/kentrosistemas",
              "https://www.youtube.com/@kentrosistemas",
            ],
            contactPoint: [
              {
                "@type": "ContactPoint",
                telephone: "+55-0800-550-2020",
                contactType: "customer service",
                areaServed: "BR",
                availableLanguage: ["Portuguese"],
              },
              {
                "@type": "ContactPoint",
                telephone: "+55-11-91582-7716",
                contactType: "sales",
                areaServed: "BR",
                availableLanguage: ["Portuguese"],
              },
              {
                "@type": "ContactPoint",
                telephone: "+55-38-3220-8706",
                contactType: "sales",
                areaServed: "BR",
                availableLanguage: ["Portuguese"],
              },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: TITLE,
            description: DESC,
            url: URL,
            inLanguage: "pt-BR",
            isPartOf: {
              "@type": "WebSite",
              name: "Kentro Sistemas Inteligentes",
              url: "https://kentro.com.br",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]),
      },
    ],
  }),
  component: InstagramLanding,
});

function InstagramLanding() {
  return (
    <CampaignLanding
      source="instagram"
      eyebrow="Veio do Instagram?"
      headline={
        <>
          Transforme seguidores em clientes com{" "}
          <span className="gradient-text">Chat + CRM + IA</span>
        </>
      }
      subheadline="Atenda Direct, WhatsApp, voz e e-mail no mesmo lugar. A Kentro responde rápido pelo seu negócio e ainda organiza o funil de vendas."
      highlights={[
        "Direct do Instagram + WhatsApp em uma única caixa de entrada.",
        "IA responde dúvidas, qualifica e marca reuniões 24/7.",
        "Cada conversa vira um lead organizado no CRM.",
        "Campanhas de Stories e anúncios viram vendas rastreáveis.",
        "Resposta em segundos — sem perder cliente para o concorrente.",
      ]}
      quickBullets={QUICK_BULLETS}
      faqs={FAQS}
      whatsappMessage="Olá! Vim pelo Instagram e quero saber mais."
    />
  );
}
